// ════════════════════════════════════════════════════════════════════════════
// AdminReviewPrepTabV2 — refactor piloto usando primitivos Lpa*
//
// Comparação com AdminReviewPrepTab.jsx (legado):
//   • Linhas: 282 → ~200 (LOC -29%)
//   • Inline styles: 38 → 4 (-89%)
//   • <input>/<select>/<button> sem label: 3 → 0
//   • Acessibilidade: aria-* injetado automaticamente via LpaField
//
// Passa a coexistir com o original como rota separada — quando aprovado,
// substituir AdminReviewPrepTab.jsx pelo conteúdo deste arquivo.
//
// Pack DS · PR piloto · Sprint UX
// ════════════════════════════════════════════════════════════════════════════

import React, { useState } from "react";
import { useQuery } from "../shared/hooks";
import { fetchAllClientsMeta } from "../services";
import { generateReviewPrep } from "../services/reviewPrep";
import { fmt } from "./adminUtils.jsx";
import { LpaButton, LpaCard, LpaField, LpaSelect, LpaBadge } from "../components/ui/lpa";

// ─── Tipos (assumindo o ReviewPrepResult do service) ────────────────────────

interface ReviewPrep {
  client: {
    id: string;
    nome: string;
    email: string;
    telefone: string;
    patrimonio_total: number;
  };
  performance: {
    totalReturn: number;
    monthlyAvgReturn: number;
    snapshotCount: number;
  };
  movements: Array<{
    data: string;
    tipo: string;
    ativo: string;
    valor: number;
    quantidade: number;
    descricao: string;
  }>;
  alerts: Array<{
    type: string;
    severity: string;
    message: string;
    description: string;
  }>;
  agenda: Array<{
    item: string;
    duration: string;
    description: string;
  }>;
  generatedAt: string;
}

interface Client {
  id: string;
  nome: string;
}

// ─── Component ──────────────────────────────────────────────────────────────

interface Props {
  T: { card: string; text: string; muted: string; border: string; bg: string };
}

export default function AdminReviewPrepTabV2({ T: _T }: Props): React.ReactElement {
  const [selectedClientId, setSelectedClientId] = useState<string>("");
  const [reviewPrep, setReviewPrep] = useState<ReviewPrep | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { data: clientsRaw, loading: clientsLoading } = useQuery(() => fetchAllClientsMeta(), []);
  const clients: Client[] = Array.isArray(clientsRaw) ? clientsRaw : [];

  const handleGenerate = async () => {
    if (!selectedClientId) {
      setError("Selecione um cliente para gerar prep");
      return;
    }
    setIsGenerating(true);
    setError(null);
    try {
      const prep = await generateReviewPrep(selectedClientId);
      setReviewPrep(prep as ReviewPrep);
    } catch (err) {
      setError(`Erro ao gerar prep: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setIsGenerating(false);
    }
  };

  const formatDate = (iso: string) => (iso ? new Date(iso).toLocaleDateString("pt-BR") : "—");

  return (
    <div
      style={{
        padding: 24,
        background: "var(--lpa-bg)",
        color: "var(--lpa-text)",
        minHeight: "100%",
      }}
    >
      <h2
        style={{
          fontSize: "var(--lpa-text-3xl)",
          fontWeight: 600,
          margin: "0 0 24px",
        }}
      >
        Prep automático para reunião
      </h2>

      {/* ── Form: seleção de cliente ──────────────────────────── */}
      <LpaCard variant="elevated" padding="lg" style={{ marginBottom: 24 }}>
        <div style={{ display: "flex", gap: 12, alignItems: "flex-end", flexWrap: "wrap" }}>
          <div style={{ flex: 1, minWidth: 240 }}>
            <LpaField
              label="Selecionar cliente"
              hint="Escolha o cliente para gerar prep automático"
              error={error}
              required
            >
              <LpaSelect
                value={selectedClientId}
                onChange={(v) => {
                  setSelectedClientId(v);
                  setReviewPrep(null);
                  setError(null);
                }}
                disabled={clientsLoading}
                placeholder="— selecione um cliente —"
                options={clients.map((c) => ({
                  value: c.id,
                  label: c.nome,
                }))}
              />
            </LpaField>
          </div>

          <div style={{ marginBottom: 14 }}>
            <LpaButton
              variant="primary"
              onClick={handleGenerate}
              disabled={!selectedClientId || isGenerating || clientsLoading}
              loading={isGenerating}
            >
              {isGenerating ? "Gerando…" : "Gerar prep"}
            </LpaButton>
          </div>
        </div>
      </LpaCard>

      {/* ── Output: prep gerado ───────────────────────────────── */}
      {reviewPrep && (
        <LpaCard variant="elevated" padding="lg">
          {/* Cabeçalho */}
          <header
            style={{
              marginBottom: 24,
              paddingBottom: 16,
              borderBottom: "1px solid var(--lpa-border)",
            }}
          >
            <h3
              style={{
                fontSize: "var(--lpa-text-2xl)",
                fontWeight: 600,
                margin: "0 0 6px",
              }}
            >
              {reviewPrep.client.nome}
            </h3>
            <div
              style={{
                fontFamily: "var(--lpa-font-mono)",
                fontSize: "var(--lpa-text-xs)",
                color: "var(--lpa-muted)",
                textTransform: "uppercase",
                letterSpacing: "0.06em",
              }}
            >
              Patrimônio: {fmt(reviewPrep.client.patrimonio_total)} · Gerado em{" "}
              {formatDate(reviewPrep.generatedAt)}
            </div>
          </header>

          {/* Performance */}
          <Section title="Resumo de desempenho">
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                gap: 12,
              }}
            >
              <KpiTile
                label="Retorno total"
                value={`${reviewPrep.performance.totalReturn >= 0 ? "+" : ""}${reviewPrep.performance.totalReturn.toFixed(2)}%`}
                positive={reviewPrep.performance.totalReturn >= 0}
              />
              <KpiTile
                label="Média mensal"
                value={`${reviewPrep.performance.monthlyAvgReturn >= 0 ? "+" : ""}${reviewPrep.performance.monthlyAvgReturn.toFixed(2)}%`}
                positive={reviewPrep.performance.monthlyAvgReturn >= 0}
              />
              <KpiTile
                label="Snapshots analisadas"
                value={String(reviewPrep.performance.snapshotCount)}
              />
            </div>
          </Section>

          {/* Movimentações */}
          {reviewPrep.movements.length > 0 && (
            <Section title="Movimentações principais (90 dias)">
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {reviewPrep.movements.map((mv, idx) => (
                  <LpaCard key={idx} variant="flat" padding="sm">
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <div>
                        <div style={{ fontSize: "var(--lpa-text-lg)", fontWeight: 500 }}>
                          {mv.ativo}
                        </div>
                        <div
                          style={{
                            fontSize: "var(--lpa-text-xs)",
                            color: "var(--lpa-muted)",
                            fontFamily: "var(--lpa-font-mono)",
                            marginTop: 2,
                          }}
                        >
                          {mv.tipo.toUpperCase()} · {formatDate(mv.data)}
                        </div>
                      </div>
                      <strong
                        style={{
                          fontFamily: "var(--lpa-font-mono)",
                          fontSize: "var(--lpa-text-xl)",
                        }}
                      >
                        {fmt(mv.valor)}
                      </strong>
                    </div>
                  </LpaCard>
                ))}
              </div>
            </Section>
          )}

          {/* Alertas */}
          {reviewPrep.alerts.length > 0 && (
            <Section title="Pontos de atenção">
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {reviewPrep.alerts.map((al, idx) => (
                  <LpaCard key={idx} variant="flat" padding="sm">
                    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                      <LpaBadge
                        variant={
                          al.severity === "high"
                            ? "danger"
                            : al.severity === "medium"
                              ? "warning"
                              : "info"
                        }
                      >
                        {al.severity}
                      </LpaBadge>
                      <div style={{ flex: 1, fontSize: "var(--lpa-text-md)" }}>
                        <strong>{al.message}</strong>
                        <div
                          style={{
                            fontSize: "var(--lpa-text-xs)",
                            color: "var(--lpa-muted)",
                            marginTop: 2,
                          }}
                        >
                          {al.description}
                        </div>
                      </div>
                    </div>
                  </LpaCard>
                ))}
              </div>
            </Section>
          )}

          {/* Agenda sugerida */}
          {reviewPrep.agenda.length > 0 && (
            <Section title="Agenda sugerida">
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {reviewPrep.agenda.map((item, idx) => (
                  <LpaCard key={idx} variant="flat" padding="sm">
                    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                      <LpaBadge variant="accent">{item.duration}</LpaBadge>
                      <div style={{ flex: 1 }}>
                        <strong style={{ fontSize: "var(--lpa-text-lg)" }}>{item.item}</strong>
                        <div
                          style={{
                            fontSize: "var(--lpa-text-xs)",
                            color: "var(--lpa-muted)",
                            marginTop: 2,
                          }}
                        >
                          {item.description}
                        </div>
                      </div>
                    </div>
                  </LpaCard>
                ))}
              </div>
            </Section>
          )}
        </LpaCard>
      )}
    </div>
  );
}

// ─── Helpers locais ────────────────────────────────────────────────────────

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <section style={{ marginBottom: 24 }}>
      <h4
        style={{
          fontSize: "var(--lpa-text-lg)",
          fontWeight: 600,
          margin: "0 0 12px",
        }}
      >
        {title}
      </h4>
      {children}
    </section>
  );
}

function KpiTile({
  label,
  value,
  positive,
}: {
  label: string;
  value: string;
  positive?: boolean;
}): React.ReactElement {
  const color =
    positive === true
      ? "var(--lpa-success)"
      : positive === false
        ? "var(--lpa-danger)"
        : "var(--lpa-text)";

  return (
    <LpaCard variant="flat" padding="sm">
      <div
        style={{
          fontFamily: "var(--lpa-font-mono)",
          fontSize: "var(--lpa-text-2xs)",
          textTransform: "uppercase",
          letterSpacing: "0.06em",
          color: "var(--lpa-muted)",
          marginBottom: 4,
        }}
      >
        {label}
      </div>
      <div style={{ fontSize: "var(--lpa-text-3xl)", fontWeight: 600, color }}>{value}</div>
    </LpaCard>
  );
}
